import React, { useEffect, useRef, useState } from "react";
import { COMMENT_MAX_LENGTH, validateCommentText } from "../utils/checklistComments";

const TEXTAREA_ID = "checklistCommentComposerTextarea";
const HELP_ID = "checklistCommentComposerHelp";
const VALIDATION_ID = "checklistCommentComposerValidation";

/**
 * The composer pinned below the comment list (TRD §2.2.4, §3.9 OQ4
 * ordering). Prop-driven and self-contained: it owns only the draft text,
 * never the request. `onPost` receives the already-trimmed text; the
 * caller (ChecklistComments, CLCMT-F006) owns the API call, `isSubmitting`,
 * and any `serverError` to display.
 *
 * Design note for the container (CLCMT-F006): this component never clears
 * its own draft on success — only a failed submission's text must survive
 * (AC-F2.7, AC-F2.8), and never auto-clearing satisfies that by
 * construction. To clear the field after a successful post, remount the
 * composer (e.g. `<ChecklistCommentComposer key={successCount} .../>`)
 * rather than adding a "clear" prop — this keeps the reset entirely
 * declarative and avoids an effect racing `isSubmitting`/`serverError`
 * across two separate prop updates.
 *
 * Props:
 * - onPost:        (trimmedText: string) => void
 * - isSubmitting:  true while the caller's create request is in flight
 * - serverError:   string | null — rendered inline, never a toast
 * - onCancel:      () => void | undefined — when provided, a Cancel button
 *                  is rendered beside Post (used when the composer is shown
 *                  on demand behind an "Add a comment" toggle)
 */
const ChecklistCommentComposer = ({
  onPost,
  isSubmitting,
  serverError,
  onCancel,
}) => {
  const [draft, setDraft] = useState("");
  const [touched, setTouched] = useState(false);
  // Belt-and-braces guard against a double-click issuing two requests before
  // `isSubmitting` has had a chance to propagate back down (AC-F2.5) — the
  // same pattern as StatusChangeNotesModal.js's submittingRef.
  const submittingRef = useRef(false);

  // Release the in-flight guard once the caller's request settles.
  useEffect(() => {
    if (!isSubmitting) {
      submittingRef.current = false;
    }
  }, [isSubmitting]);

  const validation = validateCommentText(draft);
  const isInvalid = !validation.isValid;
  const remaining = COMMENT_MAX_LENGTH - draft.length;
  const showValidation = touched && isInvalid;

  const handleChange = (event) => {
    // maxLength on the textarea already stops typed input; the hard slice
    // additionally stops a paste from exceeding the limit (AC-F2.3 — the
    // StatusChangeNotesModal.js:147-149 precedent).
    setDraft(event.target.value.slice(0, COMMENT_MAX_LENGTH));
  };

  const handlePost = () => {
    if (isSubmitting || submittingRef.current) return;
    if (isInvalid) {
      setTouched(true);
      return;
    }
    submittingRef.current = true;
    onPost(draft.trim());
  };

  const postDisabled = isInvalid || Boolean(isSubmitting);

  return (
    <div className="checklist-comment-composer">
      <label
        htmlFor={TEXTAREA_ID}
        className="checklist-comment-composer-label"
      >
        Add a comment
      </label>
      <textarea
        id={TEXTAREA_ID}
        className="form-control checklist-comment-composer-textarea"
        rows={3}
        maxLength={COMMENT_MAX_LENGTH}
        value={draft}
        aria-describedby={showValidation ? `${HELP_ID} ${VALIDATION_ID}` : HELP_ID}
        aria-invalid={showValidation}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        disabled={isSubmitting}
      />
      <div className="checklist-comment-composer-meta" id={HELP_ID}>
        <span
          className="checklist-comment-composer-remaining"
          aria-live="polite"
        >
          {remaining} characters remaining
        </span>
      </div>
      {showValidation ? (
        <div
          className="checklist-comment-composer-validation"
          id={VALIDATION_ID}
          role="alert"
        >
          Enter a comment before posting.
        </div>
      ) : null}
      {serverError ? (
        <div className="checklist-comment-composer-server-error" role="alert">
          {serverError}
        </div>
      ) : null}
      <div className="checklist-comment-composer-actions">
        {onCancel ? (
          <button
            type="button"
            className="btn checklist-comment-composer-cancel"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          className="btn primary-btn checklist-comment-composer-post"
          style={{ minHeight: 44, minWidth: 44 }}
          disabled={postDisabled}
          onClick={handlePost}
        >
          {isSubmitting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
};

export default ChecklistCommentComposer;
